// 島カルーセル（v0.7.2）。フォーカス中の島（フル詳細）の「奥・左右」に、隣の島を
// 簡易表現（小惑星ディスク＋ぼんやりした飾りの点）で見切れさせる presentation 層。
//
// 設計方針（性能と「他の島も後ろにちょっと映ってる」感の両立）:
//   - フル詳細の庭ビュー（garden_view / customer_view）はフォーカス島だけが持つ（diorama.island）。
//   - 隣の島は「簡易ディスク」= 小惑星の上面に似た円盤 + デコ位置にパステルの小球を散らすだけ。
//   - 未アンロックの「次の島」は霧がかったゴーストディスク（薄い・彩度低）で「増やせる余地」を示唆。
//   - カルーセルはリング状に配置し、島送りでリングを回す（グイン＝quick ease + わずかな
//     オーバーシュート）。回り切ったらスナップ。回転中は簡易表現のみ＝軽い。
//
// この層は sim に触れない描画専用。main が島データ（庭グリッド）と現在 view を渡す。

import * as THREE from 'three';
import { PALETTE } from '../palette';
import type { GardenGrid, IslandShape } from '../sim/garden';
import { GRID_SIZE, shapeForIslandIndex, shapeOutline, TWIN_GEOM, TWIN_HEIGHT } from '../sim/garden';
import { getDeco } from '../sim/decos';
import { gridToWorld, GARDEN_Y } from './diorama';

/** カルーセルのリング半径（フォーカス島中心からスロット中心までの水平距離、ワールド単位）。 */
const RING_RADIUS = 13.5;
/** 見切れスロット（隣島）の縮小率と減光（奥行き表現）。 */
const NEIGHBOR_SCALE = 0.5;
const NEIGHBOR_DIM = 0.62;

/** デコ ID → 簡易ディスク上の小球の色（パステル。属性の主色をざっくり当てる）。 */
function blobColor(decoId: string): string {
  const d = getDeco(decoId);
  if (!d) return '#C9B8E8';
  const a = d.attributes;
  if ((a.crystal ?? 0) > 0) return PALETTE.crystalBlue;
  if ((a.water ?? 0) > 0) return '#9DD6F0';
  if ((a.light ?? 0) > 0) return PALETTE.warmOrange;
  if ((a.green ?? 0) > 0) return '#9CCB8A';
  if ((a.scent ?? 0) > 0) return '#F0C0D0';
  if ((a.lively ?? 0) > 0) return '#FFE3A8';
  if ((a.calm ?? 0) > 0) return '#C9B8E8';
  return '#CFC3DD';
}

export interface IslandCarousel {
  /** scene に add する root（diorama.scene に乗せる。島の自転とは独立）。 */
  root: THREE.Group;
  /**
   * カルーセルの状態を更新する（v0.7.2）。
   *
   * @param islandGardens アンロック済み各島の庭グリッド（index = 島 index）。
   * @param viewIndex     現在フォーカス中の島 index（この島はフル詳細側が描くのでディスクは出さない）。
   * @param showGhost     未アンロックの「次の島」をゴーストディスクで見せるか。
   */
  setIslands(islandGardens: GardenGrid[], viewIndex: number, showGhost: boolean): void;
  /**
   * リングの回転（グイン）を開始する。dir=+1 で次の島側へ、-1 で前の島側へ回す。
   * 回転アニメは update が進める（オーバーシュート → スナップ）。
   */
  spin(dir: 1 | -1): void;
  /** 毎フレーム（累計秒）。リングの回転イージングと浮遊を進める。 */
  update(tSec: number): void;
  dispose(): void;
}

/**
 * 簡易ディスク 1 枚（小惑星上面＋デコ小球）を作る。garden が null ならゴースト島。
 * v0.7.3: 島の形状（shape）で上面の輪郭を変える（少なくとも輪郭が違うと分かる程度）。
 * round は従来の円柱、非 round は shapeOutline から押し出した板にする。ゴーストは round 扱い。
 */
function buildDisc(garden: GardenGrid | null, ghost: boolean, shape: IslandShape = 'round'): THREE.Group {
  const g = new THREE.Group();

  const topMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(ghost ? '#8E84B0' : PALETTE.asteroidTop),
    roughness: 1,
    metalness: 0,
    flatShading: true,
    transparent: true,
    opacity: ghost ? 0.32 : 0.95,
  });

  const baseMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(ghost ? '#5C5280' : PALETTE.asteroidSide),
    roughness: 1,
    metalness: 0,
    flatShading: true,
    transparent: true,
    opacity: ghost ? 0.28 : 0.95,
  });

  // 上面の板。round は低い円柱、twin は 2 ディスク＋橋、他の非 round は輪郭から押し出した板。
  if (shape === 'twin') {
    // 双子島（v0.7.4）: カルーセルの見切れディスクでも「丸 2 つ＋橋」が分かるように。
    const VIS = 1.04;
    const cx = TWIN_GEOM.centerX * VIS;
    const r = TWIN_GEOM.radius * VIS;
    // v0.7.6: 段違いの双子島と分かる程度に左右へ軽い高低差（左 +delta / 右 -delta）。橋は簡易のまま。
    const delta = TWIN_HEIGHT.delta;
    for (const sx of [-cx, cx]) {
      const dy = sx < 0 ? delta : -delta;
      const disc = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.82, 1.2, 12, 1), topMat);
      disc.position.set(sx, GARDEN_Y - 0.6 + dy, 0);
      g.add(disc);
      const cone = new THREE.Mesh(new THREE.ConeGeometry(r * 0.82, 3.2, 8, 1), baseMat);
      cone.position.set(sx, GARDEN_Y - 2.6 + dy, 0);
      cone.scale.y = -1;
      g.add(cone);
    }
    // 橋（簡易）: 段違いの 2 島を結ぶよう少し傾けた 1 枚板。
    const bridge = new THREE.Mesh(
      new THREE.BoxGeometry(cx * 2 + 0.4, 0.7, TWIN_GEOM.bridgeHalfW * 2 * VIS),
      topMat,
    );
    bridge.position.set(0, GARDEN_Y - 0.6, 0);
    bridge.rotation.z = Math.atan2(-2 * delta, cx * 2); // 左(高)→右(低)の傾き
    g.add(bridge);
  } else if (shape === 'round') {
    const top = new THREE.Mesh(new THREE.CylinderGeometry(5.0, 4.3, 1.2, 10, 1), topMat);
    top.position.y = GARDEN_Y - 0.6;
    g.add(top);
    const base = new THREE.Mesh(new THREE.ConeGeometry(4.0, 4.0, 9, 1), baseMat);
    base.position.y = GARDEN_Y - 3.4;
    base.scale.y = -1;
    g.add(base);
  } else {
    const VIS = 1.04;
    const outline = shapeOutline(shape, 40).map((p) => new THREE.Vector2(p.x * VIS, p.z * VIS));
    const topGeo = new THREE.ExtrudeGeometry(new THREE.Shape(outline), { depth: 1.2, bevelEnabled: false, steps: 1 });
    topGeo.rotateX(Math.PI / 2);
    topGeo.computeVertexNormals();
    const top = new THREE.Mesh(topGeo, topMat);
    top.position.y = GARDEN_Y - 0.6;
    g.add(top);
    const base = new THREE.Mesh(new THREE.ConeGeometry(4.0, 4.0, 9, 1), baseMat);
    base.position.y = GARDEN_Y - 3.4;
    base.scale.y = -1;
    g.add(base);
  }

  if (ghost) {
    // ゴースト島は「？」を示す淡い発光リングだけ載せる（増やせる余地の示唆）。
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.4, 0.12, 6, 18),
      new THREE.MeshBasicMaterial({ color: new THREE.Color('#D9C6F0'), transparent: true, opacity: 0.3 }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = GARDEN_Y + 0.1;
    g.add(ring);
    return g;
  }

  // 庭デコの位置にパステルの小球を散らす（簡易表現）。多すぎると重いので上限を設ける。
  if (garden) {
    let count = 0;
    for (let idx = 0; idx < garden.length && count < 40; idx++) {
      const cell = garden[idx];
      if (!cell) continue;
      const { x, z } = gridToWorld(idx % GRID_SIZE, Math.floor(idx / GRID_SIZE));
      const blob = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.22, 0),
        new THREE.MeshStandardMaterial({
          color: new THREE.Color(blobColor(cell.decoId)),
          roughness: 0.8,
          metalness: 0,
          flatShading: true,
        }),
      );
      blob.position.set(x, GARDEN_Y + 0.15, z);
      g.add(blob);
      count++;
    }
  }
  return g;
}

function disposeGroup(o: THREE.Object3D): void {
  o.traverse((c) => {
    const m = c as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else mat?.dispose();
  });
}

export function createCarousel(): IslandCarousel {
  const root = new THREE.Group();
  // リングノード: これを Y 回転させてグインと島を送る。スロットはこの子。
  const ring = new THREE.Group();
  root.add(ring);

  // スロット = リング上のディスク置き場。各島 index に 1 スロット（フォーカス島は空き）。
  interface Slot {
    index: number; // 島 index（ゴーストは -1）
    angle: number; // リング上の角度（フォーカスを 0 とした相対）
    group: THREE.Group; // ディスク本体（縮小・減光済み）
  }
  let slots: Slot[] = [];

  // 回転イージング（グイン）の状態。
  let ringAngle = 0; // 現在のリング回転（rad）
  let targetAngle = 0; // 目標回転
  let animating = false;
  let animT0 = 0;
  let animFrom = 0;
  // 「グイン」: 円盤が回り込む手応えを出すため ~0.44s かけて回し、オーバーシュート付きで
  // スナップする（一瞬で終わらせない）。main の islandBounce（SPIN_DUR と揃える）も同尺。
  const SPIN_DUR = 0.44; // 回す時間（秒）。「グイン」と回り込む尺。

  function clearSlots(): void {
    for (const s of slots) {
      ring.remove(s.group);
      disposeGroup(s.group);
    }
    slots = [];
  }

  /**
   * スロット間の角度ステップ。アンロック島が少ないときも「奥に少し見切れる」感を出すため、
   * 隣を左右斜め後ろ（±約 50°）に置く。島が多ければ均等割りに近づける。
   */
  function slotStep(n: number): number {
    if (n <= 1) return 0;
    // 円環だが、見切れを「後ろ左右」に出したいので 1 ステップ ~50°〜（2π/n を上限）にする。
    return Math.min((Math.PI * 2) / n, (50 * Math.PI) / 180);
  }

  function setIslands(islandGardens: GardenGrid[], viewIndex: number, showGhost: boolean): void {
    clearSlots();
    const n = islandGardens.length + (showGhost ? 1 : 0);
    const step = slotStep(n);

    // フォーカス島（viewIndex）を角度 0（手前中央）に置き、他をリング上に並べる。
    // 角度はフォーカスからの「島インデックス差」に比例（隣は ±step、その先は ±2step…）。
    for (let i = 0; i < islandGardens.length; i++) {
      if (i === viewIndex) continue; // フォーカス島はフル詳細側が描く（ディスク無し）
      const angle = (i - viewIndex) * step;
      const group = buildDisc(islandGardens[i], false, shapeForIslandIndex(i));
      placeSlot(group, angle);
      ring.add(group);
      slots.push({ index: i, angle, group });
    }
    if (showGhost) {
      const angle = (islandGardens.length - viewIndex) * step; // 次の島の位置（末尾の先）
      const group = buildDisc(null, true);
      placeSlot(group, angle);
      ring.add(group);
      slots.push({ index: -1, angle, group });
    }
    // フォーカスが変わったら、フォーカス島が角度 0 に来るようリングを即座に合わせる。
    ringAngle = 0;
    targetAngle = 0;
    animating = false;
    applyRing();
  }

  /** スロット（ディスク）をリング上の角度に置く（縮小・減光・奥行きの Y 下げ）。 */
  function placeSlot(group: THREE.Group, angle: number): void {
    group.scale.setScalar(NEIGHBOR_SCALE);
    group.userData.angle = angle;
    // 減光（奥行き）: 簡易ディスクのマテリアル色を暗く寄せる。
    group.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
      if (m && 'color' in m && !m.userData?.dimmed) {
        m.color.multiplyScalar(NEIGHBOR_DIM);
        (m.userData ||= {}).dimmed = true;
      }
    });
  }

  /** ring の現在角度を各スロットのワールド位置へ反映する（手前ほど大きく＝奥行き）。 */
  function applyRing(): void {
    for (const s of slots) {
      const a = s.angle + ringAngle;
      // リング: 手前中央(a=0)を基準に、横は sin、奥行きは (1-cos) で後ろへ。
      const x = Math.sin(a) * RING_RADIUS;
      const z = -(1 - Math.cos(a)) * RING_RADIUS * 0.7; // 奥（-Z）へ引っ込む
      s.group.position.set(x, 0, z);
      // 手前に近いほど大きく・明るく見せる（|a| が小さいほど手前）。
      const near = Math.max(0, Math.cos(a)); // 0..1
      s.group.scale.setScalar(NEIGHBOR_SCALE * (0.85 + near * 0.35));
      // 真後ろ寄りは隠す（描画コスト・ごちゃつき回避）。
      s.group.visible = Math.cos(a) > -0.2;
    }
  }

  function spin(dir: 1 | -1): void {
    // dir=+1（次の島へ）= フォーカスが index+1 へ → 見た目はリングを -step ぶん回して
    // 次の島を手前へ持ってくる。step は現在のスロット数から。
    const n = slots.length + 1; // +1 はフォーカス島ぶん
    const step = slotStep(n);
    animFrom = ringAngle;
    targetAngle = ringAngle - dir * step;
    animT0 = -1; // 次の update で now を入れる
    animating = true;
  }

  /**
   * グイン easing（quick-out + 明確なオーバーシュート）。t:0..1 → 0..1。
   *
   * back-ease-out のオーバーシュート量 s を強め（2.4）にして、円盤が目標を一度行き過ぎてから
   * 戻る「弾み（グイン）」を効かせる（s=1.7 のときの ~1.10 → s=2.4 で ~1.14 まで行き過ぎる）。
   * 序盤は速く立ち上がり（回り込みが素早く始まる）、終盤でオーバーシュート→スナップする。
   */
  function easeGrin(t: number): number {
    if (t >= 1) return 1;
    const s = 2.4; // オーバーシュート量（大きいほど弾む）
    const u = t - 1;
    return 1 + (s + 1) * u * u * u + s * u * u;
  }

  function update(tSec: number): void {
    if (animating) {
      if (animT0 < 0) animT0 = tSec;
      const t = Math.min(1, (tSec - animT0) / SPIN_DUR);
      const e = easeGrin(t);
      ringAngle = animFrom + (targetAngle - animFrom) * e;
      if (t >= 1) {
        ringAngle = targetAngle;
        animating = false;
      }
    }
    applyRing();
    // 隣島をふわっと上下に漂わせる（生きてる感）。
    for (const s of slots) {
      s.group.position.y = Math.sin(tSec * 0.8 + s.index * 1.3) * 0.18;
    }
  }

  function dispose(): void {
    clearSlots();
  }

  return { root, setIslands, spin, update, dispose };
}
